import type { ChangeAttentionKind, ChangeOrder, ChangeStage, IntacctCo } from '@/lib/apiClient';
import { DATE_INPUT_MIN, fmtDate, isRealYmd, parseLocalDate } from '@/lib/dates';
import { easternTodayYmd } from '@/modules/manpower/lib/gridDates';

/**
 * What the Changes tab shows for a change's stage, kept out of the component
 * because the tab imports through the `@/components` barrel, which a spec cannot
 * load (see changesTabInvalidation.spec.ts). Everything that decides what a row
 * says lives here, where it can be tested.
 */

// The acronym leads because it is what PMs call a change and what the dialogs and the training
// guide say ("it becomes a COR"); the words after it say what that stage means.
export const STAGE_LABEL: Record<ChangeStage, string> = {
  PCO: 'PCO · Potential',
  COR: 'COR · With the client',
  OCO: 'OCO · Accepted',
  ended: 'Declined',
};

/** What an ended change reads as when it never went to the client: nothing was declined, it was dropped. */
export const WITHDRAWN_LABEL = 'Withdrawn';

/**
 * An ended change that never reached the client.
 *
 * Sending a change retypes it to COR and accepting retypes it to OCO, but declining leaves `type`
 * alone, so a rejected change still typed PCO was closed from the PCO stage: it was withdrawn, not
 * declined. A row rejected before stages existed reads the same way, which is also what its type
 * meant then: a PCO was a potential change, not yet a request the client could turn down.
 */
export function isWithdrawn(c: Pick<ChangeOrder, 'type' | 'status'> & { stage?: ChangeStage }): boolean {
  return stageOf(c) === 'ended' && c.type === 'PCO';
}

/**
 * The stage the API reports, with a fallback that mirrors the API's own rule for a
 * response that predates the field (an API rolled back behind a newer web build).
 * `approved` means the client accepted, whatever `type` still says: it was set once
 * when the change was opened and never moved.
 */
export function stageOf(c: Pick<ChangeOrder, 'type' | 'status'> & { stage?: ChangeStage }): ChangeStage {
  if (c.stage) return c.stage;
  if (c.status === 'approved') return 'OCO';
  if (c.status === 'rejected') return 'ended';
  return c.type === 'PCO' ? 'PCO' : 'COR';
}

/**
 * The whole days from a calendar date to today, never negative.
 *
 * "Today" is the Eastern business day, the same one the API stamps dates with. The browser's own
 * date would read a West Coast PM's 9 PM as the same day while the server's has already rolled over.
 */
function daysBetween(fromYmd: string, now: Date): number | null {
  const start = parseLocalDate(fromYmd);
  const today = parseLocalDate(easternTodayYmd(now));
  if (!start || !today) return null;
  return Math.max(0, Math.round((today.getTime() - start.getTime()) / 86_400_000));
}

/** "today", "1 day", "12 days". */
export function dayCount(n: number): string {
  if (n === 0) return 'today';
  return n === 1 ? '1 day' : `${n} days`;
}

/**
 * The calendar date the change entered its current stage, as YYYY-MM-DD.
 *
 * A DATE column (sent, accepted) is already a calendar day. A timestamp (opened,
 * decided) is a real moment, so it is reduced to the day it fell on in EASTERN, the same
 * business day the API uses: `slice(0, 10)` would read a 9 PM Eastern click as tomorrow.
 */
export function stageStartedOn(c: ChangeOrder): string | null {
  switch (stageOf(c)) {
    case 'PCO': return easternDay(c.createdAt);
    case 'COR': return c.sentToClientOn ?? easternDay(c.createdAt);
    case 'OCO': return c.acceptedOn ?? easternDay(c.decidedAt);
    default: return easternDay(c.decidedAt);
  }
}

/** A timestamp as the Eastern business day it fell on, or null when it is missing or unreadable. */
function easternDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : easternTodayYmd(d);
}

/** Whole days in the current stage, or null when no start date can be worked out. */
export function daysInStage(c: ChangeOrder, now: Date = new Date()): number | null {
  const start = stageStartedOn(c);
  return start ? daysBetween(start, now) : null;
}

export type StageTone = 'open' | 'client' | 'accepted' | 'ended';

export interface StageView {
  stage: ChangeStage;
  label: string;
  tone: StageTone;
  /** The line under the badge: how long, since when, and (for an accepted change) the client's reference. */
  detail: string;
}

export function stageView(c: ChangeOrder, now: Date = new Date()): StageView {
  const stage = stageOf(c);
  const days = daysInStage(c, now);
  switch (stage) {
    case 'PCO':
      return { stage, label: STAGE_LABEL.PCO, tone: 'open', detail: days == null ? '' : `open ${dayCount(days)}` };
    case 'COR': {
      const since = c.sentToClientOn ? `sent ${fmtDate(c.sentToClientOn)}` : 'sent';
      return { stage, label: STAGE_LABEL.COR, tone: 'client', detail: days == null ? since : `${since} · ${dayCount(days)}` };
    }
    case 'OCO': {
      const on = c.acceptedOn ? `accepted ${fmtDate(c.acceptedOn)}` : 'accepted';
      return { stage, label: STAGE_LABEL.OCO, tone: 'accepted', detail: c.clientRef ? `${on} · ${c.clientRef}` : on };
    }
    default: {
      // The Eastern day it was decided, like every other date on this row: the browser's own
      // zone would show a West Coast PM a different day from the one the API stamped.
      const closedOn = stageStartedOn(c);
      const on = closedOn ? `closed ${fmtDate(closedOn)}` : 'closed';
      return {
        stage,
        label: isWithdrawn(c) ? WITHDRAWN_LABEL : STAGE_LABEL.ended,
        tone: 'ended',
        detail: c.decisionNote ? `${on} · ${c.decisionNote}` : on,
      };
    }
  }
}

/**
 * Can the PM freeze Intacct's current amount for this change?
 *
 * True when the link still resolves and Intacct shows an amount, and either the
 * counted amount has drifted from it, or NOTHING was ever frozen: a change linked
 * before the stage columns existed (or while Intacct showed no total) has a null
 * counted amount, `countedAmountDiffers` is false for it by definition, and without
 * this clause there would be no button to ever give it one.
 */
export function canTakeSageAmount(c: ChangeOrder): boolean {
  if (stageOf(c) !== 'OCO' || !c.intacctEnteredAt || c.intacctCoFound !== true || c.intacctCoAmount == null) return false;
  return c.countedAmount == null || c.countedAmountDiffers === true;
}

/**
 * Sage change orders worth suggesting for a change: not already linked, and within
 * a dollar of the amount the PM typed. A dollar is the same line the tab already
 * draws for "amounts differ", so a suggestion never comes with a warning attached.
 * Closest first, then by id.
 *
 * ⚠️ A suggestion, never a link. Linking still needs the PM's click, and the API
 * still insists the change order exists on the job.
 */
export const SAGE_MATCH_TOLERANCE_DOLLARS = 1;

export function suggestedSageMatches(changeAmount: number | null, cos: IntacctCo[]): IntacctCo[] {
  if (changeAmount == null) return [];
  return cos
    .filter((c) => !c.linked && c.amount != null && Math.abs(c.amount - changeAmount) < SAGE_MATCH_TOLERANCE_DOLLARS)
    .sort((a, b) =>
      Math.abs((a.amount as number) - changeAmount) - Math.abs((b.amount as number) - changeAmount)
      || a.coId.localeCompare(b.coId));
}

/** Preselect only when there is exactly one suggestion: two candidates means the PM has to look. */
export function defaultSagePick(changeAmount: number | null, cos: IntacctCo[]): string {
  const s = suggestedSageMatches(changeAmount, cos);
  return s.length === 1 ? s[0].coId : '';
}

/**
 * Why a stage date cannot be submitted, or null when it can. Mirrors the API (a real
 * calendar day, no earlier than 2000, not in the future), so a bad date is caught on
 * the form instead of coming back as a 400. The API stays the gate.
 *
 * "Today" is the Eastern business day, the same one the API uses (`easternTodayYmd`
 * mirrors it), not the browser's own date: a PM east or west of Eastern would otherwise be
 * allowed a date the API refuses, or refused one it accepts.
 */
export function stageDateError(value: string, today: string = easternTodayYmd()): string | null {
  if (!value) return 'Pick a date.';
  if (!isRealYmd(value)) return 'That is not a real date.';
  if (value < DATE_INPUT_MIN) return 'That date is too far back.';
  if (value > today) return 'That date has not happened yet.';
  return null;
}


/* ── The nudge: changes that have sat too long ─────────────────────────────── */

/**
 * What a nudged change should do next. The API decides WHICH changes are nudged and why
 * (`attention` on each row): one definition of "sat too long", shared with the Today page, so
 * nothing here compares a day count to a threshold. This only words it.
 */
export const ATTENTION_TEXT: Record<ChangeAttentionKind, string> = {
  send: 'Send it to the client, or withdraw it',
  chase: 'Follow up with the client',
  link: 'Link it to Sage: it is not counted until then',
};

/** "Follow up with the client · 9 days", or null for a change nobody needs to chase. */
export function attentionLine(c: Pick<ChangeOrder, 'attention'>): string | null {
  const a = c.attention;
  return a ? `${ATTENTION_TEXT[a.kind]} · ${dayCount(a.days)}` : null;
}

/** How many changes carry a nudge, and how long the longest has waited. */
export function nudgeSummary(changes: Array<Pick<ChangeOrder, 'attention'>>): { count: number; longest: number } {
  let count = 0;
  let longest = 0;
  for (const c of changes) {
    if (!c.attention) continue;
    count += 1;
    longest = Math.max(longest, c.attention.days);
  }
  return { count, longest };
}

/* ── Smaller checks on the dates a PM enters and the time the client takes ──── */

/**
 * The sent date, when the accepted date a PM is about to enter falls before it, else null.
 *
 * A WARNING, never a block: a verbal yes before the paperwork went out is legitimate, and the API
 * deliberately accepts it. It is the usual slip (a date typed into the wrong field), so the
 * dialog says so, once, and lets the PM go on.
 */
export function acceptedBeforeSent(c: Pick<ChangeOrder, 'sentToClientOn'>, acceptedOn: string): string | null {
  const sent = c.sentToClientOn;
  if (!sent || !acceptedOn || !isRealYmd(acceptedOn)) return null;
  return acceptedOn < sent ? sent : null;
}

/**
 * Average whole days the client took to ACCEPT: from the day a change was sent to the day they accepted.
 *
 * ⚠️ ACCEPTED changes only, and only ones that were SENT. This tile used to measure from when a change
 * was opened to when it was decided, which is the PM's pricing time plus the client's, and which read a
 * change opened straight at OCO as an instant decision. A change with no sent date (opened past COR,
 * accepted straight from a PCO, or marked sent before the stage columns existed) has no client wait to
 * measure and is left out, so the figure can be absent rather than wrong.
 *
 * Declined changes are NOT counted, deliberately: "Declined" is also how a PM withdraws a change they
 * had sent, and the two cannot be told apart, so a change dropped after a month would read as the client
 * taking a month to say no.
 *
 * The end is the date the client accepted (the date the change counts on), not the day somebody clicked.
 * Never negative: a yes before the paperwork went out is zero days.
 */
export function avgDaysToAccept(changes: ChangeOrder[]): number | null {
  const waits: number[] = [];
  for (const c of changes) {
    if (stageOf(c) !== 'OCO' || !c.sentToClientOn) continue;
    const accepted = c.acceptedOn ?? easternDay(c.decidedAt);
    const from = parseLocalDate(c.sentToClientOn);
    const to = accepted ? parseLocalDate(accepted) : null;
    if (!from || !to) continue;
    waits.push(Math.max(0, Math.round((to.getTime() - from.getTime()) / 86_400_000)));
  }
  if (!waits.length) return null;
  return Math.round(waits.reduce((a, b) => a + b, 0) / waits.length);
}
